// TEA's TEAL Accountability app exposes three similar CCMR Excel exports that are easy to
// confuse. Only the CCMR Verifier feeds A-F (see isXlsxCcmrVerifier). The other two are the wrong
// file for an upload here, and both auto-detected as a Verifier under the old loose header check:
//   - CCMR Tracker: monitors currently-enrolled grade 9-12 students (grade-band/indicator tabs
//     like "09-12 CCMR", "Grad & Non-Grad CCMR", "...IBC/CTE/Combo"). A monitoring tool, not an
//     accountability source.
//   - CCMR Outcomes Bonus: an HB3 funding report (single "CCMR_Outcomes_Bonus_<district>" tab,
//     "CCMR OB Criteria Met" columns).
// This detector recognizes those so the uploader can point the user straight at the Verifier
// instead of failing server-side with a generic error.

// cell.text throws on merged cells whose master value is null, so derive text from cell.value.
const cellText = (cell) => {
  const value = cell ? cell.value : null;
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join('');
    }
    if (value.text != null) {
      return String(value.text);
    }
    return '';
  }
  return String(value);
};

const VERIFIER_HINT =
  'Upload the "College, Career, and Military Readiness (CCMR) Data" verifier file instead (it has ' +
  'a Glossary tab plus one tab per 9-digit campus number), downloaded from the CCMR Verifier in ' +
  'the TEAL Accountability app.';

const isWrongCcmrFile = (file) =>
  new Promise((resolve) => {
    const isXlsxFile =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      (file.name || '').toLowerCase().endsWith('.xlsx');
    if (!isXlsxFile) {
      resolve({ isWrong: false });
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve({ isWrong: false });
    reader.onload = async () => {
      try {
        const exceljsModule = await import('exceljs/dist/exceljs.min.js');
        const ExcelJS = exceljsModule.default || exceljsModule;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(reader.result);

        const sheetNames = workbook.worksheets.map((sheet) => (sheet.name || '').trim());
        // A real verifier has per-campus tabs; if one exists this isn't a wrong-CCMR file — leave
        // it for isXlsxCcmrVerifier to validate.
        if (sheetNames.some((name) => /^\d{9}$/.test(name))) {
          resolve({ isWrong: false });
          return;
        }

        const allHeaders = new Set();
        workbook.worksheets.forEach((sheet) => {
          const headerRow = sheet.getRow(1);
          for (let col = 1; col <= sheet.columnCount; col += 1) {
            allHeaders.add(cellText(headerRow.getCell(col)).trim());
          }
        });

        // A genuine, ingestable Outcomes Bonus file is caught earlier by isCcmrOutcomesBonus (in
        // determineFileType, which runs that validator BEFORE this one) and never reaches here.
        // Defense-in-depth: only flag an OB-shaped file as "wrong" when it is INCOMPLETE — i.e. it
        // looks OB-ish but does not carry the full v1 OB accept signature (the three band columns
        // plus "CCMR OB Criteria Met"). When all four are present it's a valid OB upload, so fall
        // through rather than resolving isWrong here.
        const looksLikeOutcomesBonus =
          sheetNames.some((name) => /outcomes\s*bonus/i.test(name)) || allHeaders.has('CCMR OB Criteria Met');
        const hasFullOutcomesBonusSignature =
          allHeaders.has('CCMR OB Criteria Met') &&
          allHeaders.has('Econ Disadv Graduate') &&
          allHeaders.has('Non-Econ Disadv Graduate') &&
          allHeaders.has('Special Ed Graduate');
        if (looksLikeOutcomesBonus && !hasFullOutcomesBonusSignature) {
          resolve({
            isWrong: true,
            kind: 'CCMR Outcomes Bonus',
            message: `This looks like the CCMR Outcomes Bonus report (HB3 funding), not the CCMR Verifier. ${VERIFIER_HINT}`,
          });
          return;
        }

        // Tracker tabs are organized by grade band / indicator rather than by campus.
        const looksLikeTracker =
          sheetNames.some((name) => /ccmr/i.test(name) && /(09-?12|non-?grad|grad)/i.test(name)) ||
          (allHeaders.has('CCMR Met') && sheetNames.some((name) => /\b(IBC|CTE|Combo)\b/i.test(name)));
        if (looksLikeTracker) {
          resolve({
            isWrong: true,
            kind: 'CCMR Tracker',
            message: `This looks like the CCMR Tracker, which monitors currently-enrolled grade 9-12 students and is not used for accountability, not the CCMR Verifier. ${VERIFIER_HINT}`,
          });
          return;
        }

        // CCMR-shaped (both hallmark columns) but neither a verifier nor an identifiable Tracker /
        // Outcomes Bonus — still not ingestable here.
        if (allHeaders.has('CCMR Met') && allHeaders.has('Excluded from Accountability')) {
          resolve({
            isWrong: true,
            kind: 'CCMR (non-verifier)',
            message: `This does not look like the CCMR Verifier workbook. ${VERIFIER_HINT}`,
          });
          return;
        }

        resolve({ isWrong: false });
      } catch (error) {
        resolve({ isWrong: false });
      }
    };
    reader.readAsArrayBuffer(file);
  });

export default isWrongCcmrFile;
